import {PI_CARDINAL} from '../pi/piPaths';
import type {PlushState} from './PaiPlush';
import {KnitBeanie, PlushFace, PlushFrame} from './plushKit';

/**
 * Ollie: a sea otter plush hugging a cardinal die the way real otters keep
 * a favorite rock — its lucky die. Warm brown fur, a cream muzzle with a
 * button nose and whisker dots, little round ears, and a flat tail. It
 * shares Beanie's body shape, so it can wear Beanie's hat; the ears then
 * move down to peek out under the cuff.
 */

const FUR = {light: '#C9A077', mid: '#A87A52', dark: '#7E5638'};
const DARK = '#6E4A2F';
const MUZZLE = '#EBD6BC';
const BELLY = '#D2B08A';
const NOSE = '#3A2418';

/** Props for {@link OlliePlush}. */
export interface OlliePlushProps {
  /** Rendered width in px. */
  size: number;
  /** Expression to show. Omitted defaults to `idle`. */
  state?: PlushState;
  /** Puts Beanie's knit hat on it. Omitted means no hat. */
  hasBeanie?: boolean;
}

/**
 * Renders Ollie (see module comment). Decorative (`aria-hidden`).
 *
 * @param props - See {@link OlliePlushProps}.
 * @return The mascot SVG.
 * @throws Never.
 */
export function OlliePlush({
  size,
  state = 'idle',
  hasBeanie = false,
}: OlliePlushProps) {
  const ears = hasBeanie
    ? [
        {cx: 34, cy: 82},
        {cx: 166, cy: 82},
      ]
    : [
        {cx: 50, cy: 52},
        {cx: 150, cy: 52},
      ];
  return (
    <PlushFrame
      size={size}
      state={state}
      furStops={FUR}
      eyeLineFraction={94 / 220}
      thoughtAnchor={{x: 156, y: 44}}
    >
      {kit => (
        <>
          <g filter={kit.fuzz}>
            <ellipse
              cx={170}
              cy={190}
              rx={28}
              ry={10}
              fill={DARK}
              transform="rotate(-28 170 190)"
            />
            {ears.map(({cx, cy}) => (
              <circle key={cx} cx={cx} cy={cy} r={13} fill={kit.fur} />
            ))}
            <ellipse cx={76} cy={198} rx={18} ry={9} fill={DARK} />
            <ellipse cx={124} cy={198} rx={18} ry={9} fill={DARK} />
            <path
              d="M100 30 C150 30 176 72 176 122 C176 166 146 194 100 194 C54 194 24 166 24 122 C24 72 50 30 100 30 Z"
              fill={kit.fur}
            />
          </g>
          {ears.map(({cx, cy}) => (
            <circle key={cx} cx={cx} cy={cy + 1} r={6} fill={DARK} />
          ))}
          {hasBeanie && <KnitBeanie kit={kit} color={PI_CARDINAL} />}
          <ellipse
            cx={100}
            cy={164}
            rx={42}
            ry={30}
            fill={BELLY}
            filter={kit.soft}
          />
          <ellipse
            cx={100}
            cy={108}
            rx={48}
            ry={30}
            fill={MUZZLE}
            filter={kit.soft}
          />
          <PlushFace kit={kit} x={100} y={94} scale={0.95} />
          <path
            d="M 94 101 Q 100 99 106 101 Q 104 107 100 107 Q 96 107 94 101 Z"
            fill={NOSE}
          />
          {[
            {cx: 74, cy: 114},
            {cx: 69, cy: 120},
            {cx: 126, cy: 114},
            {cx: 131, cy: 120},
          ].map(({cx, cy}) => (
            <circle key={cx} cx={cx} cy={cy} r={1.6} fill={DARK} />
          ))}
          {/* The lucky die, held to the chest. */}
          <g transform="rotate(-8 100 158)">
            <rect
              x={78}
              y={136}
              width={44}
              height={44}
              rx={10}
              fill={PI_CARDINAL}
            />
            <rect
              x={78}
              y={136}
              width={44}
              height={44}
              rx={10}
              fill="#fff"
              opacity={0.08}
            />
            {[
              {cx: 89, cy: 147},
              {cx: 100, cy: 158},
              {cx: 111, cy: 169},
            ].map(({cx, cy}) => (
              <circle key={cx} cx={cx} cy={cy} r={4} fill="#FFF6E8" />
            ))}
          </g>
          <g filter={kit.fuzz}>
            <ellipse cx={76} cy={160} rx={11} ry={13} fill={DARK} />
            <ellipse cx={124} cy={156} rx={11} ry={13} fill={DARK} />
          </g>
        </>
      )}
    </PlushFrame>
  );
}
